import React, { useEffect, useState } from 'react';
import { X, Download, Mail } from 'lucide-react';
import type { PayrollEntry, Employee } from '../types';
import { supabase } from '../lib/supabase';
import { formatLocalDate } from '../utils/payrollPeriodCalculator';

interface PayStubModalProps {
  entry: PayrollEntry;
  employee: Employee;
  onClose: () => void;
  onDownload: () => void;
  onEmail: () => void;
}

interface YTDTotals {
  grossPay: number;
  netPay: number;
  taxDeductions: number;
  insuranceDeductions: number;
  retirementDeductions: number;
  bonus: number;
  federalTax: number;
  medicareTax: number;
  socialSecurityTax: number;
  stateTax: number;
}

const PayStubModal: React.FC<PayStubModalProps> = ({ 
  entry, 
  employee, 
  onClose,
  onDownload,
  onEmail
}) => {
  const [ytdTotals, setYtdTotals] = useState<YTDTotals | null>(null);

  useEffect(() => {
    const fetchYTDTotals = async () => {
      const currentYear = new Date(entry.payPeriod.end).getFullYear();
      const { data: ytdEntries } = await supabase
        .from('payroll_entries')
        .select('*')
        .eq('employee_id', employee.id)
        .gte('pay_period_end', `${currentYear}-01-01`)
        .lte('pay_period_end', entry.payPeriod.end)
        .order('pay_period_end', { ascending: true });

      if (ytdEntries) {
        const totals = ytdEntries.reduce((acc, curr) => ({
          grossPay: acc.grossPay + curr.gross_pay,
          netPay: acc.netPay + curr.net_pay,
          taxDeductions: acc.taxDeductions + curr.tax_deduction,
          insuranceDeductions: acc.insuranceDeductions + curr.insurance_deduction,
          retirementDeductions: acc.retirementDeductions + curr.retirement_deduction,
          bonus: acc.bonus + (curr.bonus || 0),
          federalTax: acc.federalTax + curr.federal_tax,
          medicareTax: acc.medicareTax + curr.medicare_tax,
          socialSecurityTax: acc.socialSecurityTax + curr.social_security_tax,
          stateTax: acc.stateTax + curr.state_tax
        }), {
          grossPay: 0,
          netPay: 0,
          taxDeductions: 0,
          insuranceDeductions: 0,
          retirementDeductions: 0,
          bonus: 0,
          federalTax: 0,
          medicareTax: 0,
          socialSecurityTax: 0,
          stateTax: 0
        });
        setYtdTotals(totals);
      }
    };

    fetchYTDTotals();
  }, [entry, employee.id]);

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const regularPay = entry.grossPay - (entry.bonus || 0);
  const bonus = entry.bonus || 0;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Pay Stub</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={onDownload}
              className="text-blue-600 hover:text-blue-800"
              title="Download PDF"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onEmail}
              className="text-blue-600 hover:text-blue-800"
              title="Email Paystub"
            >
              <Mail className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-600">
              Pay Period: {formatLocalDate(entry.payPeriod.start)} - {formatLocalDate(entry.payPeriod.end)}
            </p>
            {entry.payDate && (
              <p className="text-sm text-gray-900 font-semibold mt-1">
                Pay Date: {formatLocalDate(entry.payDate)}
              </p>
            )}
          </div>
          <div className="grid grid-cols-[2fr,1fr,1fr] gap-1">
            <div>
              <h3 className="text-lg font-semibold mb-2">Employee Information</h3>
              <div className="space-y-1">
                <p><span className="font-medium">Name:</span> {employee.firstName} {employee.lastName}</p>
                <p><span className="font-medium">ID:</span> {employee.id}</p>
                <p><span className="font-medium">Position:</span> {employee.position}</p>
                <p><span className="font-medium">Department:</span> {employee.department}</p>
              </div>
            </div>
            <div className="col-span-2">
              <h3 className="text-lg font-semibold mb-2 text-right pr-8">Payment Information</h3>
              <div className="space-y-1 text-right pr-8">
                <p><span className="font-medium">Payment Type:</span> {employee.paymentType}</p>
                <p><span className="font-medium">Rate:</span> ${employee.salary.toFixed(2)}{employee.paymentType === 'hourly' ? '/hr' : '/year'}</p>
                <p><span className="font-medium">State:</span> {employee.state}</p>
                <p><span className="font-medium">Tax Status:</span> {employee.taxStatus}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-lg font-semibold mb-3">Earnings & Deductions</h3>
            <div className="grid grid-cols-[2fr,1fr,1fr] gap-1">
              <div className="font-medium text-gray-700">Category</div>
              <div className="font-medium text-gray-700 text-right pr-8">Current</div>
              <div className="font-medium text-gray-700 text-right pr-8">YTD</div>

              <div className="contents">
                <div>Regular Pay</div>
                <div className="text-right pr-8">${formatCurrency(regularPay)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals ? ytdTotals.grossPay - ytdTotals.bonus : 0)}</div>

                {bonus > 0 && (
                  <>
                    <div className="text-green-600">Bonus</div>
                    <div className="text-right text-green-600 pr-8">${formatCurrency(bonus)}</div>
                    <div className="text-right text-green-600 pr-8">${formatCurrency(ytdTotals?.bonus || 0)}</div>
                  </>
                )}

                <div className="font-medium border-t border-gray-200 pt-2">Total Gross Pay</div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">${formatCurrency(entry.grossPay)}</div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">${formatCurrency(ytdTotals?.grossPay || 0)}</div>
              </div>

              <div className="contents">
                <div className="font-medium text-gray-500 border-t border-gray-200 pt-4 mt-2">Tax Deductions</div>
                <div className="border-t border-gray-200 pt-4 mt-2"></div>
                <div className="border-t border-gray-200 pt-4 mt-2"></div>

                <div className="pl-4">Federal Tax</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.federalTax)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.federalTax || 0)}</div>

                <div className="pl-4">Social Security Tax</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.socialSecurityTax)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.socialSecurityTax || 0)}</div>

                <div className="pl-4">Medicare Tax</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.medicareTax)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.medicareTax || 0)}</div>

                <div className="pl-4">State Tax</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.stateTax)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.stateTax || 0)}</div>

                <div className="font-medium border-t border-gray-200 pt-2">Total Tax</div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">${formatCurrency(entry.deductions.tax)}</div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">${formatCurrency(ytdTotals?.taxDeductions || 0)}</div>
              </div>

              <div className="contents">
                <div className="font-medium text-gray-500 border-t border-gray-200 pt-4 mt-2">Other Deductions</div>
                <div className="border-t border-gray-200 pt-4 mt-2"></div>
                <div className="border-t border-gray-200 pt-4 mt-2"></div>

                <div className="pl-4">Insurance</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.insurance)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.insuranceDeductions || 0)}</div>

                <div className="pl-4">Retirement</div>
                <div className="text-right pr-8">${formatCurrency(entry.deductions.retirement)}</div>
                <div className="text-right pr-8">${formatCurrency(ytdTotals?.retirementDeductions || 0)}</div>

                <div className="font-medium border-t border-gray-200 pt-2">Total Other Deductions</div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">
                  ${formatCurrency(entry.deductions.insurance + entry.deductions.retirement)}
                </div>
                <div className="font-medium border-t border-gray-200 pt-2 text-right pr-8">
                  ${formatCurrency((ytdTotals?.insuranceDeductions || 0) + (ytdTotals?.retirementDeductions || 0))}
                </div>
              </div>

              <div className="font-medium border-t border-gray-200 pt-2 mt-4">Total Deductions</div>
              <div className="font-medium border-t border-gray-200 pt-2 mt-4 text-right pr-8">
                ${formatCurrency(
                  entry.deductions.tax +
                  entry.deductions.insurance +
                  entry.deductions.retirement
                )}
              </div>
              <div className="font-medium border-t border-gray-200 pt-2 mt-4 text-right pr-8">
                ${formatCurrency(
                  (ytdTotals?.taxDeductions || 0) +
                  (ytdTotals?.insuranceDeductions || 0) +
                  (ytdTotals?.retirementDeductions || 0)
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-[2fr,1fr,1fr] gap-1">
              <div className="text-lg font-semibold">Net Pay</div>
              <div className="text-lg font-semibold text-right pr-8">${formatCurrency(entry.netPay)}</div>
              <div className="text-lg font-semibold text-right pr-8">${formatCurrency(ytdTotals?.netPay || 0)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayStubModal;